/** 원본 뷰어가 다루는 파일 종류. 서버가 돌려준 mime_type으로 고르고, 없으면 파일명 확장자로 본다. */
export const FILE_KIND = { PDF: 'pdf', IMAGE: 'image', SHEET: 'xlsx', LEGACY_SHEET: 'xls', UNKNOWN: 'unknown' };

const KIND_BY_MIME = {
    'application/pdf': FILE_KIND.PDF,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': FILE_KIND.SHEET,
    'application/vnd.ms-excel': FILE_KIND.LEGACY_SHEET,
};
const KIND_BY_EXTENSION = { pdf: FILE_KIND.PDF, xlsx: FILE_KIND.SHEET, xls: FILE_KIND.LEGACY_SHEET, png: FILE_KIND.IMAGE, jpg: FILE_KIND.IMAGE, jpeg: FILE_KIND.IMAGE };

export const fileKindOf = (mimeType, filename) => {
    const mime = String(mimeType ?? '').toLowerCase();
    if (mime.startsWith('image/')) return FILE_KIND.IMAGE;
    if (KIND_BY_MIME[mime]) return KIND_BY_MIME[mime];
    const ext = /\.([^.]+)$/.exec(String(filename ?? '').toLowerCase())?.[1];
    return KIND_BY_EXTENSION[ext] ?? FILE_KIND.UNKNOWN;
};

export const FILE_KIND_LABEL = { [FILE_KIND.PDF]: 'PDF', [FILE_KIND.IMAGE]: 'Image', [FILE_KIND.SHEET]: 'Spreadsheet', [FILE_KIND.LEGACY_SHEET]: 'Spreadsheet (.xls)' };

/** 만료 30초 전부터 만료로 본다. 발급 시각과 화면 시계가 조금 어긋나도 만료된 URL로 요청하지 않게 한다. */
export const FILE_URL_EXPIRY_SKEW_MS = 30 * 1000;

/** 서버가 준 expires_at(ISO, UTC)까지 남은 시간(ms). 이미 지났거나 읽을 수 없으면 0 — 그 URL은 실패하면 다시 받는다. */
export const msUntilFileUrlExpiry = (expiresAt, now = Date.now(), skewMs = FILE_URL_EXPIRY_SKEW_MS) => {
    const at = Date.parse(expiresAt ?? '');
    if (Number.isNaN(at)) return 0;
    return Math.max(0, at - skewMs - now);
};

/** 화면에 그리는 행·열 상한. 격자는 가상화 없이 그리므로 그 너머는 다운로드로 본다. 서식만 있는 빈 열이 수백 개인 시트도 있다. */
export const SHEET_PREVIEW_MAX_ROWS = 500;
export const SHEET_PREVIEW_MAX_COLUMNS = 60;

/** 1 → A, 27 → AA. 엑셀 열 머리글. */
export const columnLetter = (index) => {
    let n = index;
    let out = '';
    while (n > 0) {
        const rem = (n - 1) % 26;
        out = String.fromCharCode(65 + rem) + out;
        n = Math.floor((n - 1) / 26);
    }
    return out;
};

/** 엑셀 셀 값을 화면 글자로. 수식은 계산 결과, 서식 있는 글은 이어 붙인 글, 날짜는 YYYY-MM-DD. */
export const cellText = (value) => {
    if (value == null) return '';
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? '' : value.toISOString().slice(0, 10);
    if (typeof value === 'object') {
        if (Array.isArray(value.richText)) return value.richText.map((r) => r?.text ?? '').join('');
        if ('result' in value) return cellText(value.result);
        if ('text' in value) return cellText(value.text);
        if ('error' in value) return String(value.error);
        return '';
    }
    return String(value);
};

/**
 * exceljs 워크시트를 SheetViewer가 그리는 격자 { name, columns, rows: [{ row, cells }] }로 바꾼다.
 * 빈 행은 건너뛰지 않는다 — 셀 주소(A12)로 그리드 라인과 짚어야 하므로 행 번호가 실제 번호와 같아야 한다.
 */
export const worksheetToSheet = (worksheet, maxRows = SHEET_PREVIEW_MAX_ROWS, maxColumns = SHEET_PREVIEW_MAX_COLUMNS) => {
    const columnCount = Math.min(Math.max(1, worksheet.actualColumnCount ?? worksheet.columnCount ?? 1), maxColumns);
    const rowCount = Math.min(worksheet.actualRowCount ?? worksheet.rowCount ?? 0, maxRows);
    const columns = Array.from({ length: columnCount }, (_, i) => columnLetter(i + 1));
    const rows = [];
    for (let r = 1; r <= rowCount; r += 1) {
        const row = worksheet.getRow(r);
        rows.push({ row: r, cells: columns.map((_, c) => cellText(row.getCell(c + 1)?.value)) });
    }
    return { name: worksheet.name, columns, rows, truncated: (worksheet.actualRowCount ?? worksheet.rowCount ?? 0) > maxRows };
};

/** .xlsx를 시트별 격자 목록으로. CI와 PL이 다른 시트에 있는 문서도 라인이 읽힌 시트를 바로 연다. exceljs는 커서 필요할 때만 불러온다. */
export async function workbookToSheets(arrayBuffer) {
    const { default: ExcelJS } = await import('exceljs');
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(arrayBuffer);
    const sheets = workbook.worksheets.filter((ws) => (ws.actualRowCount ?? ws.rowCount) > 0).map((ws) => worksheetToSheet(ws));
    if (sheets.length === 0 && workbook.worksheets[0]) sheets.push(worksheetToSheet(workbook.worksheets[0]));
    return sheets;
}

/** 첫 번째(내용 있는) 워크시트만 격자로. */
export async function workbookToSheet(arrayBuffer) {
    const sheets = await workbookToSheets(arrayBuffer);
    return sheets[0] ?? null;
}

/** 원문 위치가 가리키는 시트의 번호. 못 찾으면 null(보던 시트 유지). 공백·대소문자는 무시한다. */
export const sheetIndexFor = (sheets, sheetName) => {
    if (!sheetName || !Array.isArray(sheets)) return null;
    const wanted = String(sheetName).trim().toLowerCase();
    const index = sheets.findIndex((sh) => String(sh?.name ?? '').trim().toLowerCase() === wanted);
    return index >= 0 ? index : null;
};

/** 다운로드 프레임을 치우기까지 기다리는 시간. 큰 파일도 이 안에 브라우저가 받기 시작한다. */
export const DOWNLOAD_FRAME_TTL_MS = 60 * 1000;

/**
 * 받은 다운로드 URL을 숨은 프레임에 연다. Content-Disposition: attachment라 화면 이동 없이 파일이 저장된다.
 * 새 탭(window.open·target=_blank)을 쓰지 않는 것은, URL을 서버에서 받아 오는 사이 클릭 직후의 허용 시간이 지나
 * 팝업 차단에 걸리기 때문이다. URL이 거절되면(만료·권한) 오류 페이지는 숨은 프레임 안에 머물고 화면은 그대로다.
 */
export const openDownloadUrl = (url) => {
    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.title = 'packing-list-download';
    frame.style.display = 'none';
    frame.src = url;
    document.body.appendChild(frame);
    setTimeout(() => frame.remove(), DOWNLOAD_FRAME_TTL_MS);
};
